import React, { useState } from "react";

interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartData[];
  size: number;
}

const PieChart: React.FC<PieChartProps> = ({ data, size }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = data.reduce((sum, item) => sum + item.value, 0);
  let startAngle = 0;
  const strokeWidth = size * 0.15;
  const hoverStrokeWidth = size * 0.18;
  const radius = (size - strokeWidth) / 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="overflow-visible"
    >
      <g transform={`translate(${size / 2}, ${size / 2})`}>
        {data.map((item, index) => {
          const angle = (item.value / total) * 360;
          const endAngle = startAngle + angle;
          const x1 = Math.cos((startAngle * Math.PI) / 180) * radius;
          const y1 = Math.sin((startAngle * Math.PI) / 180) * radius;
          const x2 = Math.cos((endAngle * Math.PI) / 180) * radius;
          const y2 = Math.sin((endAngle * Math.PI) / 180) * radius;

          const largeArcFlag = angle > 180 ? 1 : 0;

          const pathData = [
            `M ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            `L ${x2} ${y2}`,
          ].join(" ");

          const midAngle = startAngle + angle / 2;
          const labelX = Math.cos((midAngle * Math.PI) / 180) * (radius * 0.6);
          const labelY = Math.sin((midAngle * Math.PI) / 180) * (radius * 0.6);

          startAngle = endAngle;

          return (
            <g
              key={index}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <path
                d={pathData}
                fill="none"
                stroke={item.color}
                strokeWidth={
                  index === hoveredIndex ? hoverStrokeWidth : strokeWidth
                }
                strokeLinecap="round"
                style={{ transition: "stroke-width 0.3s ease" }}
              />
              <text
                x={labelX}
                y={labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="var(--secondary)"
                fontSize={size / 20}
              >
                {item.label}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

export default PieChart;
